import { InterVariableFontFamilyTheme } from 'constants/styling/typographyTheme'
import React, { Fragment, ReactNode, forwardRef, useEffect } from 'react'
import { Stack, SxProps, Theme, ThemeProvider, createTheme } from '@mui/material'

import Button from '@mui/material/Button'
import ReactLoading from 'react-loading'
import { buttonMUITheme } from 'constants/styling/buttonTheme'
import classnames from 'classnames'
import styles from './MUIButton.module.sass'

/**
 * @typedef ButtonVariant Available button variant
 */
export type ButtonVariant = 'contained' | 'outlined' | 'text'

/**
 * @typedef ButtonType Available button type
 */
export type ButtonType = 'defaultPrimary' | 'orangePrimary' | 'darkPrimary' | 'secondaryNoBorder' | 'darkSecondaryNoborder' | 'orangeSecondaryNoBorder' | 'secondaryBorder' | 'orangeSecondaryBorder' | 'greenPrimary' | 'greenSecondary'

/**
 * @typedef ButtonSize Available button size
 */
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg'

/**
 * @type ButtonBorder Available button border styles
 */
export type ButtonBorder = 'sharp' | 'rounded' | 'rounded-xs' | 'rounded-sm'

/** Side of an `iconOnly` button per size: the height of a labelled button of that size. */
const ICON_ONLY_SIDE: Record<ButtonSize, string> = {
  xs: '2.6rem',
  sm: '2.8rem',
  md: '3.6rem',
  lg: '4rem',
}

/**
 * @interface BaseProps Input properties
 */
interface BaseProps {
  /** Button id. */
  id?: string
  /** Override or extend the styles applied to the component. */
  className?: string
  /** Button type */
  type?: ButtonType
  /** Button size */
  size?: ButtonSize
  /** Whether the button is full width (100%) or not */
  fullWidth?: boolean
  /** Button border style */
  borderStyle?: ButtonBorder
  /** Button variant */
  variant?: ButtonVariant
  /** Whether the button is disabled or not */
  disabled?: boolean
  /**
   * A `disabled` button stays in the tab order: `aria-disabled` instead of the native attribute, clicks ignored.
   * For a disabled action whose reason (`aria-describedby`) keyboard and screen-reader users must still reach.
   */
  isFocusableWhenDisabled?: boolean
  /** Whether the button is disabled or not */
  isLoading?: boolean
  /** Whether the button is disabled or not */
  isIconButton?: boolean
  /** Button icon in the start position */
  startIcon?: ReactNode
  /** Button icon in the end position */
  endIcon?: ReactNode
  /** Custom tab sx styles props */
  sx?: SxProps<Theme>,
  /** Onclick action to be called */
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  /** Accessible name, for buttons whose visible label repeats across rows */
  'aria-label'?: string
  /** ARIA role, for a button acting as a radio or checkbox */
  role?: string
  'aria-checked'?: boolean
  /** Id of the element that describes the button, e.g. why it is disabled. Without it, the button's own `id` is used. */
  'aria-describedby'?: string
  /** Menu-trigger wiring, for a button that opens a list */
  'aria-haspopup'?: React.AriaAttributes['aria-haspopup']
  'aria-expanded'?: boolean
  'aria-controls'?: string
  /** Marks the element an accessible `Modal` focuses first when it opens */
  'data-autofocus'?: boolean
  'data-cy'?: string
  tabIndex?: number
  children?: ReactNode
}

/** An icon-only button has no visible text, so its accessible name is required. */
type IconOnlyProps =
  | {
    /** Square button holding only an icon (its children), sized by `size`. */
    iconOnly: true
    'aria-label': string
  }
  | { iconOnly?: false }

type Props = BaseProps & IconOnlyProps

/**
 * @component MUIButton 
 * @example
 * <MUIButton type="defaultPrimary" size="md" variant="contained" />
 */
export const MUIButton = forwardRef<HTMLButtonElement, Props>(({
  id = '',
  className = '',
  type = 'defaultPrimary',
  borderStyle = 'rounded',
  size = 'md',
  fullWidth = false,
  variant,
  disabled,
  isFocusableWhenDisabled = false,
  startIcon,
  endIcon,
  isLoading = false,
  isIconButton = false,
  iconOnly = false,
  'aria-describedby': ariaDescribedBy,
  onClick,
  children,
  sx,
  ...props
}, ref) => {
  const isInert = !!disabled && isFocusableWhenDisabled
  const ariaLabel = props['aria-label']

  useEffect(() => {
    if (import.meta.env.DEV && iconOnly && !ariaLabel) console.warn('MUIButton: an iconOnly button needs an aria-label')
  }, [iconOnly, ariaLabel])

  return (
    <ThemeProvider theme={
      createTheme({
        ...buttonMUITheme,
        ...InterVariableFontFamilyTheme,
      })
    }>
      <Button
        id={id}
        aria-describedby={ariaDescribedBy ?? id}
        className={classnames(className, styles.muiButton, styles[borderStyle])}
        onClick={e => {
          if (isInert) return e.preventDefault()
          if (!isLoading) onClick?.(e)
        }}
        variant={variant}
        size={size}
        disabled={isFocusableWhenDisabled ? undefined : disabled}
        aria-disabled={isInert || undefined}
        fullWidth={fullWidth}
        disableRipple
        sx={{
          borderWidth: '1px',
          borderStyle: 'solid',
          color: `${type}.label`,
          bgcolor: `${type}.main`,
          borderColor: `${type}.border`,
          '&:disabled': {
            bgcolor: `${type}.disabled`,
            color: `${type}.disabledLabel`,
            borderColor: `${type}.disabledWithBorder`,
          },
          '&:hover': {
            bgcolor: `${type}.hover`,
            borderColor: `${type}.borderOnHover`,
          },
          padding: isIconButton ? '.4rem' : '',
          ...(iconOnly && {
            width: ICON_ONLY_SIDE[size],
            height: ICON_ONLY_SIDE[size],
            minWidth: ICON_ONLY_SIDE[size],
            minHeight: ICON_ONLY_SIDE[size],
            padding: 0,
          }),
          ...(isFocusableWhenDisabled && {
            '&[aria-disabled="true"], &[aria-disabled="true"]:hover': {
              bgcolor: `${type}.disabled`,
              color: `${type}.disabledLabel`,
              borderColor: `${type}.disabledWithBorder`,
              cursor: 'default',
            },
          }),
          ...sx,
        }}
        ref={ref}
        {...props}
      >
        {isLoading ?
          <Stack height="2.4rem" flexDirection="row" alignItems="center">
            <ReactLoading color="currentColor" type="spin" width="2rem" height="2rem" />
          </Stack>
          :
          <Fragment>
            {!!startIcon && startIcon}
            {(!!startIcon || !!endIcon) ? <span>{children}</span> : children}
            {!!endIcon && endIcon}
          </Fragment>
        }
      </Button>
    </ThemeProvider>
  )
})
